import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CustomDropdown } from "../SpotOrders/spotOrderUi";
import SoMobileFilters from "./SoMobileFilters";
import OrdersSubTabs from "./OrdersSubTabs";
import OrderMobileCardShell from "./OrderMobileCardShell";
import AuthService from "../../../api/services/AuthService";
import { getWalletPageLabel } from "../../../utils/walletTypeLabels";

const ORDER_DESKTOP_COL_COUNT = 5;
const PAGE_SIZE = 10;

const FROM_OPTIONS = [
  { value: "all", label: "All" },
  { value: "main", label: "Main Wallet" },
  { value: "spot", label: "Spot Wallet" },
  { value: "futures", label: "Futures Wallet" },
  { value: "margin", label: "Margin Wallet" },
];

const TO_OPTIONS = [
  { value: "all", label: "All" },
  { value: "main", label: "Main Wallet" },
  { value: "spot", label: "Spot Wallet" },
  { value: "futures", label: "Futures Wallet" },
  { value: "margin", label: "Margin Wallet" },
];

/** Same shapes as withdraw get-coin parsing. */
function coinsListFromGetCoinResponse(res) {
  if (!res || res.success === false) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.coins)) return d.coins;
  if (d && Array.isArray(d.data)) return d.data;
  if (d && Array.isArray(d.list)) return d.list;
  return [];
}

/** `currency` query: ObjectId → currency_id; otherwise short_name (backend rules). */
function coinToCurrencyQueryParam(coin) {
  if (!coin) return "";
  const id = coin._id ?? coin.currency_id ?? coin.id;
  const idStr = id != null ? String(id).trim() : "";
  if (/^[a-f0-9]{24}$/i.test(idStr)) return idStr;
  return String(coin.short_name ?? coin.shortName ?? "").trim();
}

function walletTransferHistoryParse(res) {
  if (res && res.success === false) return { items: [], total: null };
  const d = res?.data;
  if (Array.isArray(d)) return { items: d, total: null };
  if (d && typeof d === "object") {
    const rawTotal = d.total ?? d.totalCount ?? d.count;
    const n = typeof rawTotal === "string" ? Number(rawTotal) : rawTotal;
    const total = Number.isFinite(n) ? n : null;
    if (Array.isArray(d.data)) return { items: d.data, total };
    if (Array.isArray(d.items)) return { items: d.items, total };
    if (Array.isArray(d.rows)) return { items: d.rows, total };
    if (Array.isArray(d.records)) return { items: d.records, total };
  }
  return { items: [], total: null };
}

function computeHasMore({ skip, batchLength, total, pageSize }) {
  if (batchLength === 0) return false;
  if (total != null) return skip + batchLength < total;
  return batchLength >= pageSize;
}

function formatTransferRowDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso).replace("T", " ").slice(0, 19);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function formatTransferAmount(amount, shortName) {
  const sym = String(shortName || "").trim();
  if (amount == null || amount === "") return sym || "—";
  const a = typeof amount === "number" ? String(amount) : String(amount).trim();
  if (!sym) return a;
  if (a.toUpperCase().includes(sym.toUpperCase())) return a;
  return `${a} ${sym}`;
}

function mapWalletTransferApiRow(raw, index) {
  const fromKey = raw.from_wallet ?? raw.fromWallet ?? "";
  const toKey = raw.to_wallet ?? raw.toWallet ?? "";
  const shortName = raw.short_name ?? raw.shortName ?? "";
  const created = raw.createdAt ?? raw.created_at ?? "";
  const id = raw.id ?? raw._id ?? `wt-${index}-${String(created)}`;

  return {
    id,
    date: formatTransferRowDate(created),
    coin: String(shortName || "").toUpperCase(),
    amount: formatTransferAmount(raw.amount, shortName),
    from: getWalletPageLabel(fromKey),
    to: getWalletPageLabel(toKey),
    fromKey: String(fromKey).trim().toLowerCase(),
    toKey: String(toKey).trim().toLowerCase(),
  };
}

const Transfer = () => {
  const [rows, setRows] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [coins, setCoins] = useState([]);

  const [from, setFrom] = useState("all");
  const [to, setTo] = useState("all");
  const [coin, setCoin] = useState("all");
  const [fromDraft, setFromDraft] = useState("all");
  const [toDraft, setToDraft] = useState("all");
  const [coinDraft, setCoinDraft] = useState("all");
  const [openDropdown, setOpenDropdown] = useState(null);

  const filtersEpochRef = useRef(0);
  const rowsRef = useRef([]);
  const hasMoreRef = useRef(true);
  const loadingInitialRef = useRef(true);
  const loadingMoreRef = useRef(false);
  const desktopSentinelRef = useRef(null);
  const mobileSentinelRef = useRef(null);

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  useEffect(() => {
    loadingInitialRef.current = loadingInitial;
  }, [loadingInitial]);

  const coinOptions = useMemo(() => {
    const base = [{ value: "all", label: "All" }];
    const sorted = [...coins].sort((a, b) =>
      String(a.short_name || a.name || "").localeCompare(String(b.short_name || b.name || ""), undefined, { sensitivity: "base" }),
    );
    for (const c of sorted) {
      const q = coinToCurrencyQueryParam(c);
      if (!q) continue;
      const label = String(c.short_name || c.name || q).toUpperCase();
      base.push({ value: q, label });
    }
    return base;
  }, [coins]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await AuthService.getAllCoins();
        const list = coinsListFromGetCoinResponse(res);
        if (!cancelled) setCoins(list);
      } catch {
        if (!cancelled) setCoins([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {
    filtersEpochRef.current += 1;
    setRows([]);
    setHasMore(true);
    hasMoreRef.current = true;
    setLoadingInitial(true);
    loadingInitialRef.current = true;
  }, [from, to, coin]);

  const fetchParams = useMemo(
    () => ({
      from_wallet: from === "all" ? undefined : from,
      to_wallet: to === "all" ? undefined : to,
      currency: coin === "all" ? undefined : coin,
      limit: PAGE_SIZE,
    }),
    [from, to, coin],
  );

  useEffect(() => {
    const epoch = filtersEpochRef.current;
    let cancelled = false;

    (async () => {
      try {
        const res = await AuthService.walletTransferHistory({
          ...fetchParams,
          skip: 0,
        });
        if (cancelled || epoch !== filtersEpochRef.current) return;
        const { items, total } = walletTransferHistoryParse(res);
        const batch = (items || []).map((raw, i) => mapWalletTransferApiRow(raw, i));
        setRows(batch);
        rowsRef.current = batch;
        const more = computeHasMore({ skip: 0, batchLength: batch.length, total, pageSize: PAGE_SIZE });
        setHasMore(more);
        hasMoreRef.current = more;
      } catch {
        if (!cancelled && epoch === filtersEpochRef.current) {
          setRows([]);
          rowsRef.current = [];
          setHasMore(false);
          hasMoreRef.current = false;
        }
      } finally {
        if (!cancelled && epoch === filtersEpochRef.current) {
          setLoadingInitial(false);
          loadingInitialRef.current = false;
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchParams, from, to, coin]);

  const loadMore = useCallback(async () => {
    const epoch = filtersEpochRef.current;
    if (!hasMoreRef.current || loadingInitialRef.current || loadingMoreRef.current) return;
    const skip = rowsRef.current.length;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const res = await AuthService.walletTransferHistory({
        ...fetchParams,
        skip,
      });
      if (epoch !== filtersEpochRef.current) return;
      const { items, total } = walletTransferHistoryParse(res);
      const batch = (items || []).map((raw, i) => mapWalletTransferApiRow(raw, skip + i));
      if (!batch.length) {
        setHasMore(false);
        hasMoreRef.current = false;
        return;
      }
      setRows((prev) => {
        const next = [...prev, ...batch];
        rowsRef.current = next;
        return next;
      });
      const more = computeHasMore({ skip, batchLength: batch.length, total, pageSize: PAGE_SIZE });
      setHasMore(more);
      hasMoreRef.current = more;
    } catch {
      setHasMore(false);
      hasMoreRef.current = false;
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [fetchParams]);

  useEffect(() => {
    const roots = [desktopSentinelRef.current, mobileSentinelRef.current].filter(Boolean);
    if (!roots.length) return undefined;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (!visible) return;
        if (!hasMoreRef.current || loadingInitialRef.current || loadingMoreRef.current) return;
        void loadMore();
      },
      { root: null, rootMargin: "160px", threshold: 0 },
    );

    roots.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [loadMore, rows.length, loadingInitial, hasMore, loadingMore]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-so-dropdown]")) setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick, true);
    return () => document.removeEventListener("click", onDocClick, true);
  }, [openDropdown]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openDropdown]);

  const renderEmptyBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <div className="so-emptyState" role="status">
          <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
        </div>
      </td>
    </tr>
  );

  const showEmpty = !loadingInitial && rows.length === 0;

  const renderLoadingBody = () => (
    <tr>
      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
        <span className="so-table__td--muted" role="status">
          Loading…
        </span>
      </td>
    </tr>
  );

  const resetFilters = () => {
    setFrom("all");
    setTo("all");
    setCoin("all");
    setFromDraft("all");
    setToDraft("all");
    setCoinDraft("all");
  };

  const applyMobileFilters = () => {
    setFrom(fromDraft);
    setTo(toDraft);
    setCoin(coinDraft);
  };

  const renderToolbar = (stacked, keyPrefix) => {
    const pref = keyPrefix ? `${keyPrefix}-` : "";
    const isMob = keyPrefix === "mob";
    const fr = isMob ? fromDraft : from;
    const tt = isMob ? toDraft : to;
    const cn = isMob ? coinDraft : coin;
    const setFr = isMob ? setFromDraft : setFrom;
    const setTt = isMob ? setToDraft : setTo;
    const setCn = isMob ? setCoinDraft : setCoin;
    return (
      <div className={`so-toolbar ${stacked ? "so-toolbar--stack" : ""}`} role="search" aria-label="Filter transfers">
        <div className="so-toolbar__left">
          <CustomDropdown
            id={`${pref}tr-dd-from`}
            label="From"
            value={fr}
            options={FROM_OPTIONS}
            onChange={setFr}
            menuKey={`${pref}tr-from`}
            openKey={openDropdown}
            onOpenToggle={setOpenDropdown}
          />
          <CustomDropdown
            id={`${pref}tr-dd-to`}
            label="To"
            value={tt}
            options={TO_OPTIONS}
            onChange={setTt}
            menuKey={`${pref}tr-to`}
            openKey={openDropdown}
            onOpenToggle={setOpenDropdown}
          />
          <CustomDropdown
            id={`${pref}tr-dd-coin`}
            label="Coin"
            value={cn}
            options={coinOptions}
            onChange={setCn}
            menuKey={`${pref}tr-coin`}
            openKey={openDropdown}
            onOpenToggle={setOpenDropdown}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="dashboard_right">
      <div className="dashboard_listing_section2 Overview_mid">
        <div className="listing_left_outer full_width transaction_history_t desktop_view2 so-desktopWrap">
          <main className="so-page" aria-labelledby="so-transfer-title">
            <h1 id="so-transfer-title" className="so-page__title">
              Transfer
            </h1>

            <OrdersSubTabs
              tabs={[
                { label: "Deposit", to: "/user_profile/orders/deposit_history" },
                { label: "Withdraw", to: "/user_profile/orders/withdraw_history" },
                { label: "Transfer", to: "/user_profile/orders/transfer" },
                // { label: "Referral", to: "/user_profile/orders/referral" },
              ]}
            />

            {renderToolbar(false, "desk")}

            <div className="so-tableWrap">
              <table className="so-table so-table--air">
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    <th scope="col">Coin</th>
                    <th scope="col">
                      Amount
                    </th>
                    <th scope="col">From</th>
                    <th scope="col">To</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingInitial
                    ? renderLoadingBody()
                    : showEmpty
                      ? renderEmptyBody()
                      : rows.map((r) => (
                        <tr key={r.id}>
                          <td className="so-num">{r.date}</td>
                          <td>{r.coin}</td>
                          <td className="so-num">
                            <strong>{r.amount}</strong>
                          </td>
                          <td>{r.from}</td>
                          <td>{r.to}</td>
                        </tr>
                      ))}
                  {!loadingInitial && !showEmpty && loadingMore ? (
                    <tr>
                      <td colSpan={ORDER_DESKTOP_COL_COUNT} className="so-table__emptyWrap">
                        <span className="so-table__td--muted" role="status">
                          Loading more…
                        </span>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            {!loadingInitial && !showEmpty ? (
              <div ref={desktopSentinelRef} className="so-transferInfiniteSentinel" aria-hidden="true" />
            ) : null}
          </main>
        </div>

        <div className="order_history_mobile_view so-mobile">
          <h2 className="so-mobile__title">Transfer</h2>
          <OrdersSubTabs
            tabs={[
              { label: "Deposit", to: "/user_profile/orders/deposit_history" },
              { label: "Withdraw", to: "/user_profile/orders/withdraw_history" },
              { label: "Transfer", to: "/user_profile/orders/transfer" },
              { label: "Referral", to: "/user_profile/orders/referral" },
            ]}
          />
          <div className="so-mobile__toolbar">
            <SoMobileFilters
              modalId="mob-transfer-filters"
              toolbar={renderToolbar(true, "mob")}
              onSearch={applyMobileFilters}
              onReset={resetFilters}
            />
          </div>
          {loadingInitial ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <span className="so-table__td--muted">Loading…</span>
            </div>
          ) : showEmpty ? (
            <div className="so-emptyState so-emptyState--mobile" role="status">
              <img src="/images/no-data.svg" alt="" width={120} height={144} className="so-emptyState__img" />
            </div>
          ) : (
            <>
              {rows.map((r) => (
                <OrderMobileCardShell key={`m-${r.id}`} desktopColumnCount={ORDER_DESKTOP_COL_COUNT} popupTitle="Transfer details">
                  <div className="so-mobileCard__row">
                    <span>Date</span>
                    <span className="so-num">{r.date}</span>
                  </div>
                  <div className="so-mobileCard__row">
                    <span>Coin</span>
                    <span>{r.coin}</span>
                  </div>
                  <div className="so-mobileCard__row">
                    <span>Amount</span>
                    <span className="so-num">{r.amount}</span>
                  </div>
                  <div className="so-mobileCard__row">
                    <span>From</span>
                    <span>{r.from}</span>
                  </div>
                  <div className="so-mobileCard__row">
                    <span>To</span>
                    <span>{r.to}</span>
                  </div>
                </OrderMobileCardShell>
              ))}
              {loadingMore ? (
                <div className="so-emptyState so-emptyState--mobile" role="status">
                  <span className="so-table__td--muted">Loading more…</span>
                </div>
              ) : null}
              <div ref={mobileSentinelRef} className="so-transferInfiniteSentinel" aria-hidden="true" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transfer;
